import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, Users, Share2, Settings, Edit, Trash2, Mail, ArrowRight, Droplet, Award, History, UserCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { BLOOD_TYPES, ORGANS } from '../mockData';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import { normalizeUserData, getUserFields, prepareForUpdate, calculateAge } from '../services/userService';
import UserInfoFields from '../components/forms/UserInfoFields';
import DonationPreferencesFields from '../components/forms/DonationPreferencesFields';
import GenderAvatar from '../components/ui/gender-avatar';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const MyProfile = () => {
  const navigate = useNavigate();
  const { user: authUser, signOut, loading } = useAuth();
  const [user, setUser] = useState(authUser || {});
  const [stats, setStats] = useState({
    donations: 0,
    livesImpacted: 0,
    network: 0,
    badges: 0
  });
  const [activeTab, setActiveTab] = useState('profile');
  const [familyMembers, setFamilyMembers] = useState([]);
  const [showFamilyDialog, setShowFamilyDialog] = useState(false);
  const [showEditFamilyDialog, setShowEditFamilyDialog] = useState(false);
  const [editingFamily, setEditingFamily] = useState(null);
  const [selectedOrgans, setSelectedOrgans] = useState([]);
  
  // Form state for controlled components
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    address: '',
    bloodType: '',
    gender: '',
    birthYear: null,
    isBloodDonor: false,
    isOrganDonor: false,
    bodyDonation: false,
    donationType: null,
    emergencyContact: {
      name: '',
      phone: '',
      relationship: 'Emergency Contact'
    }
  });

  useEffect(() => {
    // Wait for auth context to finish loading before checking authentication
    if (loading) {
      return;
    }
    
    // Redirect to sign-in if not authenticated (only after loading is complete)
    if (!authUser) {
      navigate('/signin');
      return;
    }
    
    // Normalize and set user from auth context
    const normalizedUser = normalizeUserData(authUser);
    setUser(normalizedUser);
    
      // Initialize form data with normalized user fields
      const userFields = getUserFields(normalizedUser);
      setFormData({
        name: userFields.name || '',
        phone: userFields.phone || '',
        city: userFields.city || '',
        state: userFields.state || '',
        zipCode: userFields.zipCode || '',
        country: userFields.country || '',
        address: userFields.address || '',
        bloodType: userFields.bloodType || '',
        gender: userFields.gender || '',
        birthYear: userFields.birthYear || null,
        isBloodDonor: userFields.isBloodDonor || false,
        isOrganDonor: userFields.isOrganDonor || false,
        bodyDonation: userFields.bodyDonation || false,
        donationType: userFields.donationType || null,
        emergencyContact: userFields.emergencyContact || {
          name: '',
          phone: '',
          relationship: 'Emergency Contact'
        }
      });
    
    // Initialize selectedOrgans from user data
    const organs = userFields.organsPledged || [];
    setSelectedOrgans(organs);
    
    // Fetch user stats from API
    fetchUserStats();
  }, [authUser, navigate, loading]);

  const fetchUserStats = async () => {
    try {
      // TODO: Replace with actual API call when endpoint is available
      // const response = await axios.get(`${API}/users/${user.id}/stats`);
      // setStats(response.data);
      
      // Mock stats for now
      setStats({
        donations: user.donationHistory?.length || 0,
        livesImpacted: (user.donationHistory?.length || 0) * 3, // Estimate
        network: familyMembers?.length || 0,
        badges: user.isBloodDonor && user.isOrganDonor ? 2 : (user.isBloodDonor || user.isOrganDonor ? 1 : 0)
      });
    } catch (error) {
      // Error handled silently - stats will remain at default values
    }
  };

  const donationHistory = user.donationHistory || [];

  const handleOrganToggle = (organId) => {
    setSelectedOrgans(prev =>
      prev.includes(organId)
        ? prev.filter(id => id !== organId)
        : [...prev, organId]
    );
  };

  const handleAddFamily = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const canRequestBlood = formData.get('can-request') === 'on';
    const newMember = {
      id: Date.now().toString(),
      name: formData.get('family-name'),
      relationship: formData.get('relationship'),
      phone: formData.get('family-phone'),
      canRequestBlood: canRequestBlood
    };
    
    // If can request blood, sync to emergency contact
    if (canRequestBlood) {
      const emergencyContact = {
        name: newMember.name,
        phone: newMember.phone,
        relationship: newMember.relationship
      };
      
      // Update form data
      setFormData(prev => ({
        ...prev,
        emergencyContact: emergencyContact
      }));
      
      // Update user profile via API
      try {
        const updateData = prepareForUpdate({
          emergencyContact: emergencyContact
        });
        await axios.put(`${API}/users/${user.id}`, updateData);
        
        // Update local user state
        const updatedUser = { ...user, emergency_contact: emergencyContact, emergencyContact: emergencyContact };
        setUser(updatedUser);
        localStorage.setItem('auth_user', JSON.stringify(updatedUser));
      } catch (error) {
        // If API update fails, still add family member but show warning
        toast({
          title: 'Family Member Added',
          description: 'Family member added, but emergency contact sync failed. Please update manually.',
          variant: 'destructive'
        });
      }
    }
    
    setFamilyMembers([...familyMembers, newMember]);
    setStats(prev => ({ ...prev, network: prev.network + 1 }));
    toast({ 
      title: 'Family Member Added', 
      description: canRequestBlood 
        ? 'Family member added and synced to emergency contact.' 
        : 'Family member has been added successfully.' 
    });
    setShowFamilyDialog(false);
  };

  const handleEditFamily = (member) => {
    setEditingFamily(member);
    setShowEditFamilyDialog(true);
  };

  const handleUpdateFamily = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const canRequestBlood = formData.get('edit-can-request') === 'on';
    const updatedMember = {
      name: formData.get('edit-family-name'),
      relationship: formData.get('edit-relationship'),
      phone: formData.get('edit-family-phone'),
      canRequestBlood: canRequestBlood
    };
    
    const updatedMembers = familyMembers.map(m => 
      m.id === editingFamily.id ? {
        ...m,
        ...updatedMember
      } : m
    );
    
    // If can request blood is checked, sync to emergency contact
    if (canRequestBlood) {
      const emergencyContact = {
        name: updatedMember.name,
        phone: updatedMember.phone,
        relationship: updatedMember.relationship
      };
      
      // Update form data
      setFormData(prev => ({
        ...prev,
        emergencyContact: emergencyContact
      }));
      
      // Update user profile via API
      try {
        const updateData = prepareForUpdate({
          emergencyContact: emergencyContact
        });
        await axios.put(`${API}/users/${user.id}`, updateData);
        
        // Update local user state
        const updatedUser = { ...user, emergency_contact: emergencyContact, emergencyContact: emergencyContact };
        setUser(updatedUser);
        localStorage.setItem('auth_user', JSON.stringify(updatedUser));
      } catch (error) {
        // If API update fails, still update family member but show warning
        toast({
          title: 'Family Member Updated',
          description: 'Family member updated, but emergency contact sync failed. Please update manually.',
          variant: 'destructive'
        });
        setFamilyMembers(updatedMembers);
        setShowEditFamilyDialog(false);
        setEditingFamily(null);
        return;
      }
    }
    
    setFamilyMembers(updatedMembers);
    toast({ 
      title: 'Updated', 
      description: canRequestBlood 
        ? 'Family member updated and synced to emergency contact.' 
        : 'Family member updated successfully.' 
    });
    setShowEditFamilyDialog(false);
    setEditingFamily(null);
  };

  const handleDeleteFamily = (id) => {
    setFamilyMembers(familyMembers.filter(m => m.id !== id));
    setStats(prev => ({ ...prev, network: Math.max(0, prev.network - 1) }));
    toast({ title: 'Deleted', description: 'Family member removed.' });
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEmergencyContactChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      emergencyContact: {
        ...prev.emergencyContact,
        [field]: value
      }
    }));
  };

  // Profile update moved to separate EditProfile page

  const handleSignOut = async () => {
    await signOut();
    toast({ title: 'Signed Out', description: 'You have been signed out successfully.' });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Dark Blue Header Section */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            {/* Profile Picture */}
            <div className="w-24 h-24 bg-red-700 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
              <GenderAvatar
                profilePicture={user.profilePicture}
                gender={user.gender}
                size={96}
                className="w-full h-full rounded-lg"
              />
            </div>

            {/* User Info */}
            <div className="flex-1">
              <h1 className="text-3xl md:text-4xl font-bold mb-2">{user.name || 'User'}</h1>
              <div className="flex items-center gap-2 text-blue-100">
                <Mail className="h-4 w-4" />
                <span>{user.email || 'user@example.com'}</span>
              </div>
            </div>

            {/* Sign Out Button */}
            <Button
              onClick={handleSignOut}
              variant="outline"
              className="bg-white/10 border-white/20 text-white hover:bg-white/20"
            >
              <ArrowRight className="h-4 w-4 mr-2" />
              Sign Out
            </Button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
            <div className="text-center">
              <div className="text-4xl font-bold mb-1">{stats.donations}</div>
              <div className="text-blue-200 text-sm">Donations</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold mb-1">{stats.livesImpacted}</div>
              <div className="text-blue-200 text-sm">Lives Impacted</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold mb-1">{stats.network}</div>
              <div className="text-blue-200 text-sm">Network</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold mb-1">{stats.badges}</div>
              <div className="text-blue-200 text-sm">Badges</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-transparent h-auto p-0">
              <TabsTrigger 
                value="profile" 
                className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-red-700 data-[state=active]:text-red-700 data-[state=active]:shadow-none rounded-none px-6 py-4"
              >
                <UserCircle className="h-4 w-4 mr-2" />
                Profile
              </TabsTrigger>
              <TabsTrigger 
                value="family" 
                className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-red-700 data-[state=active]:text-red-700 data-[state=active]:shadow-none rounded-none px-6 py-4"
              >
                <Users className="h-4 w-4 mr-2" />
                Family
              </TabsTrigger>
              <TabsTrigger 
                value="history" 
                className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-red-700 data-[state=active]:text-red-700 data-[state=active]:shadow-none rounded-none px-6 py-4"
              >
                <History className="h-4 w-4 mr-2" />
                History
              </TabsTrigger>
              <TabsTrigger 
                value="settings" 
                className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-red-700 data-[state=active]:text-red-700 data-[state=active]:shadow-none rounded-none px-6 py-4"
              >
                <Settings className="h-4 w-4 mr-2" />
                Settings
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          {/* Profile Tab */}
          <TabsContent value="profile" className="mt-6">
            {!user.isBloodDonor && !user.isOrganDonor ? (
              <Card className="shadow-md text-center py-12">
                <CardContent>
                  <Droplet className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Not registered as a blood donor.</h3>
                  <p className="text-gray-700 mb-6">Register now to help save lives.</p>
                  <Link to="/register">
                    <Button className="bg-red-700 hover:bg-red-900 text-white">
                      <Droplet className="h-4 w-4 mr-2" />
                      Register as Blood Donor
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Profile Card */}
                <div className="lg:col-span-1">
                  <Card className="shadow-md">
                    <CardContent className="pt-6">
                      <div className="text-center mb-6">
                        <div className="w-32 h-32 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4 overflow-hidden">
                          <GenderAvatar
                            profilePicture={user.profilePicture}
                            gender={user.gender}
                            size={128}
                            className="w-full h-full"
                          />
                        </div>
                      </div>

                      <div className="space-y-3 text-sm">
                        <div>
                          <p className="text-gray-700 font-semibold">Name</p>
                          <p className="text-gray-900">{user.name}</p>
                        </div>
                        <div>
                          <p className="text-gray-700 font-semibold">Email</p>
                          <p className="text-gray-900">{user.email}</p>
                        </div>
                        <div>
                          <p className="text-gray-700 font-semibold">Blood Type</p>
                          <Badge className="bg-red-700 text-white text-lg">{user.blood_type || user.bloodType || 'Not specified'}</Badge>
                        </div>
                        {user.birthYear && (
                          <div>
                            <p className="text-gray-700 font-semibold">Age</p>
                            <p className="text-gray-900">
                              {calculateAge(user.birthYear) !== null ? `${calculateAge(user.birthYear)} years` : 'N/A'}
                            </p>
                          </div>
                        )}
                        {user.gender && (
                          <div>
                            <p className="text-gray-700 font-semibold">Gender</p>
                            <p className="text-gray-900 capitalize">{user.gender.replace('_', ' ')}</p>
                          </div>
                        )}
                        <div>
                          <p className="text-gray-700 font-semibold">Location</p>
                          <p className="text-gray-900">
                            {user.city}
                            {user.state && `, ${user.state}`}
                            {user.zip_code || user.zipCode ? ` ${user.zip_code || user.zipCode}` : ''}
                            {`, ${user.country}`}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-700 font-semibold">Phone</p>
                          <p className="text-gray-900">{user.phone || 'Not provided'}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Main Content */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Donation Status */}
                  <Card className="shadow-md">
                    <CardHeader>
                      <CardTitle className="text-gray-900">Donation Status</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="p-4 bg-red-50 rounded-lg">
                          <div className="flex items-center mb-2">
                            <Heart className="h-5 w-5 text-red-700 mr-2" fill="currentColor" />
                            <h3 className="font-bold text-gray-900">Blood Donor</h3>
                          </div>
                          <Badge className={(user.is_blood_donor || user.isBloodDonor) ? 'bg-green-700 text-white' : 'bg-gray-600 text-white'}>
                            {(user.is_blood_donor || user.isBloodDonor) ? 'Active' : 'Not Registered'}
                          </Badge>
                        </div>
                        <div className="p-4 bg-red-50 rounded-lg">
                          <div className="flex items-center mb-2">
                            <Heart className="h-5 w-5 text-red-700 mr-2" />
                            <h3 className="font-bold text-gray-900">Organ Donor</h3>
                          </div>
                          <Badge className={(user.is_organ_donor || user.isOrganDonor) ? 'bg-green-700 text-white' : 'bg-gray-600 text-white'}>
                            {(user.is_organ_donor || user.isOrganDonor) ? 'Pledged' : 'Not Pledged'}
                          </Badge>
                        </div>
                      </div>

                      {(user.organs_pledged || user.organsPledged) && (user.organs_pledged?.length > 0 || user.organsPledged?.length > 0) && (
                        <div className="mt-4">
                          <p className="text-sm text-gray-700 font-semibold mb-2">Organs Pledged:</p>
                          <div className="flex flex-wrap gap-2">
                            {(user.organs_pledged || user.organsPledged || []).map((organId, index) => {
                              const organ = ORGANS.find(o => o.id === organId);
                              return (
                                <Badge key={index} variant="outline" className="text-gray-900 border-gray-900">
                                  {organ ? organ.name : organId}
                                </Badge>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Emergency Contact */}
                  {(user.emergency_contact || user.emergencyContact) && (
                    <Card className="shadow-md">
                      <CardHeader>
                        <CardTitle className="text-gray-900">Emergency Contact</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <p className="text-sm text-gray-700 font-semibold">Name</p>
                            <p className="text-gray-900">{(user.emergency_contact || user.emergencyContact)?.name}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-700 font-semibold">Relationship</p>
                            <p className="text-gray-900">{(user.emergency_contact || user.emergencyContact)?.relationship}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-700 font-semibold">Phone</p>
                            <p className="text-gray-900">{(user.emergency_contact || user.emergencyContact)?.phone}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Family Tab */}
          <TabsContent value="family" className="mt-6">
            <Card className="shadow-md">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-gray-900">Family Members</CardTitle>
                  <Dialog open={showFamilyDialog} onOpenChange={setShowFamilyDialog}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="bg-red-700 hover:bg-red-900 text-white">
                        <Users className="h-4 w-4 mr-2" />
                        Add Member
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-lg">
                      <DialogHeader>
                        <DialogTitle className="text-gray-900">Add Family Member</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleAddFamily} className="space-y-4">
                        <div>
                          <Label htmlFor="family-name" className="text-gray-900">Full Name</Label>
                          <Input id="family-name" name="family-name" required className="border-gray-300" />
                        </div>
                        <div>
                          <Label htmlFor="relationship" className="text-gray-900">Relationship</Label>
                          <Select name="relationship" required>
                            <SelectTrigger className="border-gray-300">
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="spouse">Spouse</SelectItem>
                              <SelectItem value="parent">Parent</SelectItem>
                              <SelectItem value="child">Child</SelectItem>
                              <SelectItem value="sibling">Sibling</SelectItem>
                              <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label htmlFor="family-phone" className="text-gray-900">Phone</Label>
                          <Input id="family-phone" name="family-phone" type="tel" required className="border-gray-300" />
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox id="can-request" name="can-request" />
                          <Label htmlFor="can-request" className="text-gray-900 font-normal">
                            Can request blood on my behalf in emergencies
                          </Label>
                        </div>
                        <Button type="submit" className="w-full bg-red-700 hover:bg-red-900 text-white">
                          Add Family Member
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {familyMembers.length > 0 ? (
                  <div className="space-y-3">
                    {familyMembers.map(member => (
                      <div key={member.id} className="p-4 border border-gray-200 rounded-md flex items-center justify-between">
                        <div className="flex-1">
                          <h4 className="font-bold text-gray-900">{member.name}</h4>
                          <p className="text-sm text-gray-700">{member.relationship} • {member.phone}</p>
                          {member.canRequestBlood && (
                            <Badge variant="outline" className="mt-2 text-green-700 border-green-700">
                              Can Request Blood
                            </Badge>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditFamily(member)}
                            className="text-gray-900 border-gray-900"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteFamily(member.id)}
                            className="text-red-700 border-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-700">No family members added yet.</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="mt-6">
            <Card className="shadow-md">
              <CardHeader>
                <CardTitle className="text-gray-900">Donation History</CardTitle>
              </CardHeader>
              <CardContent>
                {donationHistory.length > 0 ? (
                  <div className="space-y-3">
                    {donationHistory.map(donation => (
                      <div key={donation.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-md">
                        <div>
                          <h4 className="font-bold text-gray-900">{donation.type} Donation</h4>
                          <p className="text-sm text-gray-700">{donation.location}</p>
                          <p className="text-xs text-gray-600 mt-1">{new Date(donation.date).toLocaleDateString()}</p>
                        </div>
                        <Badge className="bg-red-700 text-white">{donation.units} unit{donation.units > 1 ? 's' : ''}</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-700">No donation history yet.</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="mt-6">
            <Card className="shadow-md">
              <CardHeader>
                <CardTitle className="text-gray-900">Profile Settings</CardTitle>
              </CardHeader>
              <CardContent>
                <Link to="/profile/edit">
                  <Button className="w-full bg-red-700 hover:bg-red-900 text-white">
                    <Settings className="h-4 w-4 mr-2" />
                    Edit Profile
                  </Button>
                </Link>

                <div className="mt-6 space-y-4">
                  <Link to="/badge">
                    <Button variant="outline" className="w-full text-gray-900 border-gray-900 hover:bg-gray-100">
                      <Share2 className="h-4 w-4 mr-2" />
                      Generate Digital Badge
                    </Button>
                  </Link>
                  <Link to="/network">
                    <Button variant="outline" className="w-full text-gray-900 border-gray-900 hover:bg-gray-100">
                      <Users className="h-4 w-4 mr-2" />
                      My Network
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit Family Member Dialog */}
      <Dialog open={showEditFamilyDialog} onOpenChange={setShowEditFamilyDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Edit Family Member</DialogTitle>
          </DialogHeader>
          {editingFamily && (
            <form onSubmit={handleUpdateFamily} className="space-y-4">
              <div>
                <Label htmlFor="edit-family-name" className="text-gray-900">Full Name</Label>
                <Input id="edit-family-name" name="edit-family-name" defaultValue={editingFamily.name} required className="border-gray-300" />
              </div>
              <div>
                <Label htmlFor="edit-relationship" className="text-gray-900">Relationship</Label>
                <Select name="edit-relationship" defaultValue={editingFamily.relationship} required>
                  <SelectTrigger className="border-gray-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="spouse">Spouse</SelectItem>
                    <SelectItem value="parent">Parent</SelectItem>
                    <SelectItem value="child">Child</SelectItem>
                    <SelectItem value="sibling">Sibling</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="edit-family-phone" className="text-gray-900">Phone</Label>
                <Input id="edit-family-phone" name="edit-family-phone" type="tel" defaultValue={editingFamily.phone} required className="border-gray-300" />
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox id="edit-can-request" name="edit-can-request" defaultChecked={editingFamily.canRequestBlood} />
                <Label htmlFor="edit-can-request" className="text-gray-900 font-normal">
                  Can request blood on my behalf in emergencies
                </Label>
              </div>
              <Button type="submit" className="w-full bg-red-700 hover:bg-red-900 text-white">
                Update Family Member
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyProfile;
